import { TableHeaderColumn } from '../types'

// TableHeaderColumn type description:
//
// - title: string
//     The label displayed in the table header (column name shown to the user).
//
// - key: string | null
//     The property name from the data source used for this column.
//     Must match the field in the dataset if sorting or value rendering depends on it.
//
// - secondaryKey?: string
//     Optional second property used when you need to combine two fields
//     (e.g. "surname" + "name" → full name).
//
// - type: 'string' | 'number' | 'date'
//     Defines the data type used for rendering and sorting logic.
//
// - sort: boolean
//     Enables or disables sorting for this column.
//
// - sortKey?: sortKeys
//     (Optional) Specific key used for sorting if it differs or additional from `key`.
//
// Example:
// {
//   title: 'Full Name',
//   key: 'surname',
//   secondaryKey: 'name',
//   type: 'string',
//   sort: true,
// }

// Usage guide:
//
// Define table column configuration using `TableHeaderColumn[]`
//     - Each column defines how data will be rendered and sorted.
//     - Example configurations can be found below (e.g. DriverContractsDetailsColumns).
//
//  Wrap each table component inside <TableProvider>
//     - This enables the table context and selection logic.
//     - Example:
//         <TableProvider bulk={false}>
//            <DriverContractsTable />
//         </TableProvider>
//
//  Inside the table, use the `useTable()` hook to access table context:
//     - toggleAll → select/deselect all rows
//     - toggleId → toggle a single row selection
//     - selectedGroup → stores selected IDs
//     - isSelectedAny → boolean for conditional UI (e.g. showing bulk actions)
//
//  Fetch data from your API or service layer.
//     - Example: const data = await getDriverContracts({ driverId, companyId })
//
//  Pass both the data and column definitions into your sorting hook.
//     - Example: const { sortedData, sortConfig, handleHeaderClick } = useSortableData({ data, tableColumns })
//
//  Render the table:
//     - Use <TableHeader /> to render column headers and attach sorting handlers.
//     - Use your custom <RowComponent /> (like DriverContractDetailsRow) for row rendering.
//     - Map through `sortedData` to display rows dynamically.
//
//  Result:
//     - Fully reusable and typed table system
//     - Supports sorting, dynamic columns, and row selection
//     - Works with <TableProvider> + useTable() for managing selection state
//     - Clean separation of logic: data, context, and UI layers

export const getDriverCardsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverBasics.card-number'),
    key: 'cardNumber',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverBasics.card-valid-from'),
    key: 'cardStart',
    type: 'date',
    sort: true,
  },
  {
    title: t('driverBasics.card-valid-to'),
    key: 'cardEnd',
    type: 'date',
    sort: true,
  },
]

export const getDriverContractsDetailsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  // {
  //   title: t('common.type'),
  //   key: null,
  //   type: 'string',
  //   sort: false,
  //   tooltip: t('contractDetails.contract-or-annex-type'),
  // },
  {
    title: t('contract.date-from'),
    key: 'startDate',
    type: 'date',
    sort: true,
    tooltip: t('contractDetails.contract-start-date'),
  },
  {
    title: t('contract.date-to'),
    key: 'endDate',
    type: 'date',
    sort: true,
    tooltip: t('contractDetails.contract-end-date'),
  },
  {
    title: t('contractDetails.work-system'),
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.work-system-type'),
  },
  {
    title: t('contractDetails.rate'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.base-hourly-rate'),
  },
  {
    title: t('contract.night-shifts'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.night-lump-rate'),
  },
  {
    title: t('contract.overtime'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.overtime-rate'),
  },
  {
    title: t('contractDetails.duty'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.duty-rate'),
  },
  {
    title: t('contract.bonus'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.bonus-tooltip'),
  },
  {
    title: t('contract.collective-lump-sum'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contract.collective-lump-sum'),
  },
  {
    title: t('contractDetails.eu-addition'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.eu-work-addition'),
  },
  {
    title: t('common.total'),
    key: null,
    type: 'number',
    sort: false,
    tooltip: t('contractDetails.total-hourly-rates'),
  },
  {
    title: 'Pit 2',
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.pit2-declaration'),
  },
  {
    title: t('contractDetails.under-26'),
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.under-26-tooltip'),
  },
  {
    title: t('contractDetails.senior-relief'),
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.senior-relief-tooltip'),
  },
  {
    title: 'Ppk',
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.ppk-tooltip'),
  },
  {
    title: 'Kup',
    key: null,
    type: 'string',
    sort: false,
    tooltip: t('contractDetails.kup-tooltip'),
  },
]
export const getDriversContractColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverList.last-and-first-name'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: t('contractDetails.first-contract-date'),
    key: 'contracts',
    type: 'date',
    sort: true,
  },
  {
    title: t('driverList.card-country'),
    key: 'nationality.name',
    type: 'string',
    sort: true,
  },
  { title: t('common.phone'), key: 'phoneNumber', type: 'string', sort: false },
  { title: t('common.email'), key: 'email', type: 'string', sort: false },
]
export const getDriverActivitiesColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  { title: t('driverModule.activity'), key: null, type: 'string', sort: false },
  { title: t('contract.date-from'), key: null, type: 'date', sort: false },
  { title: t('contract.date-to'), key: null, type: 'date', sort: false },
  { title: t('driverModule.duration'), key: null, type: 'string', sort: false },
  {
    title: t('driverModule.registration-number'),
    key: null,
    type: 'number',
    sort: false,
  },
  { title: t('driverModule.crew'), key: null, type: 'number', sort: false },
  { title: t('driverModule.ferry'), key: null, type: 'number', sort: false },
  { title: 'Out', key: null, type: 'number', sort: false },
  {
    title: t('driverModule.manual-entry'),
    key: null,
    type: 'number',
    sort: false,
  },
]

export const getDriversInGroupDetailsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverList.last-and-first-name'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverBasics.card-number'),
    key: null,
    type: 'string',
    sort: false,
  },
  {
    title: t('driverBasics.work-start-date'),
    key: 'workStartPermiDate',
    type: 'date',
    sort: true,
  },
]

export const getViolationsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('contract.date-from'),
    key: 'startTime',
    type: 'date',
    sort: true,
  },
  { title: t('contract.date-to'), key: 'endTime', type: 'date', sort: true },
  {
    title: t('driverModule.exceeded-by'),
    key: 'exceededBy',
    type: 'duration',
    sort: true,
  },
  { title: t('common.type'), key: 'description', type: 'string', sort: true },
  {
    title: t('driverModule.weight'),
    key: 'weight',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.charge-symbol'),
    key: 'chargeSymbol',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.driver-charge'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.company-charge'),
    key: 'companyCharge',
    type: 'number',
    sort: true,
  },
]

// @TODO CHANGE KEYS TO MATCH DATA FIELDS FOR PROPER SORTING AND RENDERING
export const getGroupedViolationsColumnsNew = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('common.driver'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.count-nn'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.count-bpn'),
    key: 'companyCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.count-pn'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.count-zn'),
    key: 'companyCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.total-violations-amount'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
]
export const getGroupedViolationsColumnsDetailsNew = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverModule.weight'),
    key: 'weight',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.violation-type'),
    key: 'type',
    type: 'number',
    sort: true,
  },
  { title: t('common.from'), key: 'startTime', type: 'date', sort: true },
  { title: t('common.to'), key: 'endTime', type: 'date', sort: true },
  {
    title: t('driverModule.exceeded-by'),
    key: 'exceededBy',
    type: 'duration',
    sort: true,
  },
  {
    title: t('driverModule.tariff'),
    key: 'chargeSymbol',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.driver-penalty'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.company-penalty'),
    key: 'companyCharge',
    type: 'number',
    sort: true,
  },
]
export const getGroupedViolationsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverList.last-and-first-name'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: t('driverModule.driver-charge'),
    key: 'driverCharge',
    type: 'number',
    sort: true,
  },
  {
    title: t('driverModule.company-charge'),
    key: 'companyCharge',
    type: 'number',
    sort: true,
  },
]

export const getDriversViolationsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('driverList.last-and-first-name'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
]

export const getBusinessTripsColumns = (
  t: (key: string) => string,
  options?: { includeAuditDates?: boolean },
): TableHeaderColumn[] => {
  const cols: TableHeaderColumn[] = [
    {
      title: t('businessTrip.trip-number'),
      key: 'number',
      type: 'string',
      sort: true,
    },
    {
      title: t('businessTrip.reg-number'),
      key: 'registrationNumber',
      type: 'string',
      sort: false,
    },
    {
      title: t('businessTrip.start'),
      key: 'startDate',
      type: 'date',
      sort: true,
    },
    { title: t('businessTrip.end'), key: 'endDate', type: 'date', sort: true },
    { title: t('common.time'), key: null, type: 'string', sort: false },
    { title: t('common.total'), key: 'amount', type: 'number', sort: true },
    {
      title: t('businessTrip.allowances'),
      key: 'parts',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.accommodations'),
      key: 'accommodations',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.costs'),
      key: 'invoices',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.advances'),
      key: 'loans',
      type: 'number',
      sort: true,
    },
    {
      title: t('common.status'),
      key: 'isTripSettled',
      type: 'string',
      sort: false,
    },
    {
      title: t('common.country'),
      key: 'destinationCountry',
      type: 'string',
      sort: false,
    },
  ]
  if (options?.includeAuditDates) {
    cols.push(
      {
        title: t('businessTrip.creation-date'),
        key: 'creationDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('businessTrip.modification-date'),
        key: 'modificationDate',
        type: 'date',
        sort: true,
      },
    )
  }
  return cols
}

export const getGroupedBusinessTripsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('common.driver'),
    key: 'surname',
    secondaryKey: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: t('businessTrip.total-time'),
    key: null,
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.total-accommodations'),
    key: 'accommodations',
    type: 'number',
    sort: true,
  },
  {
    title: t('businessTrip.total-allowances'),
    key: 'parts',
    type: 'number',
    sort: true,
  },
  {
    title: '',
    key: 'parts',
    type: 'number',
    sort: false,
  },
]

export const getBusinessTripDetailColumns = (
  t: (key: string) => string,
  options?: { includeAuditDates?: boolean },
): TableHeaderColumn[] => {
  const cols: TableHeaderColumn[] = [
    {
      title: t('businessTrip.trip-number'),
      key: 'number',
      type: 'string',
      sort: true,
    },
    {
      title: t('businessTrip.reg-number'),
      key: 'registrationNumber',
      type: 'string',
      sort: false,
    },
    {
      title: t('businessTrip.start'),
      key: 'startDate',
      type: 'date',
      sort: true,
    },
    { title: t('businessTrip.end'), key: 'endDate', type: 'date', sort: true },
    { title: t('common.time'), key: null, type: 'string', sort: false },
    {
      title: t('businessTrip.accommodations'),
      key: 'accommodations',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.costs'),
      key: 'invoices',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.advances'),
      key: 'loans',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.allowances'),
      key: 'parts',
      type: 'number',
      sort: true,
    },
    {
      title: t('businessTrip.destination-country'),
      key: 'destinationCountry',
      type: 'string',
      sort: false,
    },
  ]
  if (options?.includeAuditDates) {
    cols.push(
      {
        title: t('businessTrip.creation-date'),
        key: 'creationDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('businessTrip.modification-date'),
        key: 'modificationDate',
        type: 'date',
        sort: true,
      },
    )
  }
  return cols
}

export const getDriverViolationsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  { title: 'LP', key: null, type: 'string', sort: false },
  { title: t('driverModule.weight'), key: null, type: 'string', sort: false },
  {
    title: t('driverModule.violation-type'),
    key: null,
    type: 'string',
    sort: false,
  },
  { title: t('common.from'), key: null, type: 'string', sort: false },
  { title: t('common.to'), key: null, type: 'string', sort: false },
  {
    title: t('driverModule.exceeded-by'),
    key: null,
    type: 'string',
    sort: false,
  },
  { title: t('driverModule.tariff'), key: null, type: 'string', sort: false },
  {
    title: t('driverModule.driver-penalty'),
    key: null,
    type: 'number',
    sort: false,
  },
  {
    title: t('driverModule.company-penalty'),
    key: null,
    type: 'number',
    sort: false,
  },
]

export const getTemplatesColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('createContractTemplatePopup.name'),
    key: null,
    type: 'string',
    sort: false,
  },
  { title: t('driverList.Type'), key: null, type: 'string', sort: false },
  { title: t('driverList.WorkTime'), key: null, type: 'string', sort: false },
  { title: t('driverList.Settlement'), key: null, type: 'string', sort: false },
  { title: t('driverList.Rate'), key: null, type: 'number', sort: false },
  { title: '', key: null, type: 'string', sort: false },
]

export const getAttestationsColumns = (
  t: (key: string) => string,
  tSafe: (key: string, defaultValue: string) => string,
): TableHeaderColumn[] => [
  { title: t('common.type'), key: 'attestationType', type: 'string', sort: true },
  { title: t('common.from'), key: 'startTime', type: 'date', sort: true },
  { title: t('common.to'), key: 'endTime', type: 'date', sort: true },
  {
    title: tSafe('attestation.activity-start', 'Początek aktywności'),
    key: 'activityStartTime',
    type: 'date',
    sort: true,
  },
  {
    title: tSafe('attestation.activity-end', 'Koniec aktywności'),
    key: 'activityEndTime',
    type: 'date',
    sort: true,
  },
  {
    title: t('businessTrip.creation-date'),
    key: 'createdDate',
    type: 'date',
    sort: true,
  },
  {
    title: tSafe('attestation.accepted-date', 'Data akceptacji'),
    key: 'acceptedDate',
    type: 'date',
    sort: true,
  },
  {
    title: tSafe('attestation.issuer', 'Wystawca'),
    key: 'issuer',
    type: 'string',
    sort: true,
  },
  {
    title: tSafe('attestation.position', 'Stanowisko'),
    key: 'position',
    type: 'string',
    sort: true,
  },
]
